import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogContent, DialogTitle, Grid, IconButton, Paper, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import DispatchService from '../../setting/dispatch/DispatchService';

// "ตอนนี้ใครรับงาน" (owner 2026-11-01): one box per channel (เรือ / แอร์ × Smile / Tama) listing who
// a plain card would reach right now, in the bot's order, and underneath who would not and why.
// The backend answers with the same check the ranking uses, so what this shows is what the bot
// does. Refreshes every 30 s while open.

const POLL_MS = 30 * 1000;

const REASON = {
  disabled: 'ปิดรับงาน',
  inactive: 'ไม่ active',
  suspended: 'ถูกระงับ',
  no_channel: 'ไม่มี LINE ให้ส่ง',
  window_mode: 'ช่วงพิเศษไม่รับช่องทางนี้',
  mode_off: 'ปิดช่องทางนี้',
  shop_off: 'ปิดร้านนี้',
  big: 'ไม่รับชิ้นใหญ่',
  no_tracking: 'ไม่รับไม่มีเลขพัสดุ',
  daily_cap: 'ครบโควต้าวันนี้',
  burst: 'ได้การ์ดถี่เกิน',
  stopped_today: 'หยุดวันนี้',
  offline: 'นอกเวลา',
};

const CHANNELS = [
  { mode: 'sea', shop: 'smile', title: 'เรือ · Smile', color: '#1565C0' },
  { mode: 'sea', shop: 'tama', title: 'เรือ · Tama', color: '#1565C0' },
  { mode: 'air', shop: 'smile', title: 'แอร์ · Smile', color: '#E65100' },
  { mode: 'air', shop: 'tama', title: 'แอร์ · Tama', color: '#E65100' },
];

const t = (iso) => (iso ? format(new Date(iso), 'd MMM HH:mm') : '—');

/** What else a buyer takes — shown on the chip's tooltip. */
const extras = (b) => {
  const marks = [];
  if (!b.acceptsBig) marks.push('ไม่รับชิ้นใหญ่');
  if (b.noTracking === 'never') marks.push('ไม่รับไม่มีเลขพัสดุ');
  if (b.noTracking === 'first') marks.push('ไม่มีเลขพัสดุ: รับก่อนใคร');
  if (b.inWindow) marks.push(`ช่วงพิเศษ ${b.dailyWindow.start}–${b.dailyWindow.end} (${b.dailyWindow.mode})`);
  if (b.directFromJpy != null) marks.push(`สั่งตรง ≥ ¥${Number(b.directFromJpy).toLocaleString()}`);
  if (b.fallback) marks.push('คนสำรอง');
  return marks;
};

// Only reasons that pass with time are worth a line (owner 2026-11-01): outside their hours, a
// special window, stopped today, the burst limit, today's cap. A buyer who never takes this
// channel (switch off, other shop, no LINE) is simply not listed.
const TEMPORARY = ['offline', 'window_mode', 'stopped_today', 'burst', 'daily_cap'];

const ChannelBox = ({ channel, buyers }) => {
  const open = buyers.filter((b) => b[channel.mode][channel.shop] === null);
  const closed = buyers.filter((b) => TEMPORARY.includes(b[channel.mode][channel.shop]));
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderTop: `4px solid ${channel.color}`, height: '100%' }}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography variant="subtitle1" fontWeight={700}>{channel.title}</Typography>
        <Chip size="small" color={open.length ? 'success' : 'default'} label={`${open.length} คนรับอยู่`} />
      </Box>
      {open.length === 0 ? (
        <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>ไม่มีใครรับช่องทางนี้ตอนนี้ — การ์ดจะไม่ถูกส่ง งานจะขึ้นบอร์ดทันที</Typography>
      ) : (
        <Box display="flex" flexWrap="wrap" gap={0.75} mb={1}>
          {open.map((b, i) => (
            <Tooltip key={b.id} title={[`ลำดับ ${i + 1} · การ์ดล่าสุด ${t(b.lastCardAt)} · วันนี้ ${b.today.count} งาน`, ...extras(b)].join(' · ')}>
              <Chip
                color="success"
                variant={i === 0 ? 'filled' : 'outlined'}
                label={`${i + 1}. ${b.label}${!b.acceptsBig ? ' ·ไม่ใหญ่' : ''}${b.noTracking === 'never' ? ' ·ต้องมีเลข' : ''}`}
              />
            </Tooltip>
          ))}
        </Box>
      )}
      {closed.length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block">
          ไม่รับตอนนี้: {closed.map((b) => `${b.label} (${REASON[b[channel.mode][channel.shop]] || b[channel.mode][channel.shop]})`).join(' · ')}
        </Typography>
      )}
    </Paper>
  );
};

export default function DispatchNowSection() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await DispatchService.getNow();
      setData(res.data);
    } catch (err) {
      // transient failure keeps the last answer
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [open, load]);

  const buyers = data?.buyers || [];
  return (
    <Box mt={2}>
      <Button variant="outlined" size="small" onClick={() => setOpen(true)}>ตอนนี้ใครรับงาน</Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          ตอนนี้ใครรับงาน
          {data && <Typography variant="caption" color="text.secondary">ณ {t(data.now)}</Typography>}
          <Box flexGrow={1} />
          <IconButton size="small" onClick={() => setOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {data?.slot === 'closed' && (
            <Alert severity="warning" sx={{ mb: 1 }}>02:00–07:00 หน้าร้านปิด — บอทไม่ส่งการ์ดให้ใครในช่วงนี้ แม้คนด้านล่างจะเปิดรับ</Alert>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            การ์ดธรรมดา (ไม่ใหญ่ มีเลขพัสดุ) ตอนนี้จะถึงใคร เรียงตามลำดับที่บอทจะส่ง · กฎเดียวกับที่บอทใช้จริง
            {data?.burst ? ` · การ์ดถี่: สูงสุด ${data.burst.maxCards} ใบใน ${data.burst.windowMinutes} นาที` : ''}
          </Typography>
          {!data && <Typography variant="body2" color="text.secondary">กำลังโหลด…</Typography>}
          <Grid container spacing={1.5}>
            {CHANNELS.map((c) => (
              <Grid item xs={12} sm={6} key={c.title}>
                <ChannelBox channel={c} buyers={buyers} />
              </Grid>
            ))}
          </Grid>
          {buyers.length > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
              ชิ้นใหญ่: รับ — {buyers.filter((b) => b.acceptsBig).map((b) => b.label).join(', ') || '—'} · ไม่รับ — {buyers.filter((b) => !b.acceptsBig).map((b) => b.label).join(', ') || '—'}
              <br />
              ไม่มีเลขพัสดุ: รับก่อนใคร — {buyers.filter((b) => b.noTracking === 'first').map((b) => b.label).join(', ') || '—'} · ไม่รับ — {buyers.filter((b) => b.noTracking === 'never').map((b) => b.label).join(', ') || '—'}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
